/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useTranslation } from 'react-i18next';
import type { ShopperBasketsV2 } from '@/scapi';
import { stripCountryCode } from '@/lib/address/phone-utils';

interface OrderReviewShipToProps {
    className?: string;
    address?: ShopperBasketsV2.schemas['OrderAddress'] | null;
    /** Contact email from basket.customerInfo.email — not stored on the shipping address itself. */
    email?: string | null;
}

/**
 * Renders the shipping destination summary block in the order review header row.
 * Shows the delivery address, contact email (from customerInfo), and phone for the delivery shipment.
 * Returns null when no address is present.
 */
export default function OrderReviewShipTo({ className, address, email }: OrderReviewShipToProps) {
    const { t } = useTranslation('checkout');

    if (!address || (!address.firstName && !address.lastName && !address.address1)) {
        return null;
    }

    const nameLine = [address.firstName, address.lastName].filter(Boolean).join(' ');
    const streetLine = [address.address1, address.address2].filter(Boolean).join(' ');
    const cityLine = [address.postalCode, [address.city, address.stateCode].filter(Boolean).join(' ')]
        .filter(Boolean)
        .join(', ');

    return (
        <div className={className} data-testid="order-review-shipto">
            <p className="mb-4 text-xs uppercase tracking-wide text-muted-foreground">
                {t('reviewOrder.shipTo.heading')}
            </p>
            {nameLine && <p className="text-base leading-6 font-bold text-foreground mb-2">{nameLine}</p>}
            {streetLine && <p className="text-sm text-foreground">{streetLine}</p>}
            {cityLine && <p className="text-sm text-foreground mb-2">{cityLine}</p>}
            {email && (
                <p className="text-sm text-muted-foreground truncate" title={email}>
                    {email}
                </p>
            )}
            {address.phone && <p className="text-sm text-muted-foreground">{stripCountryCode(address.phone)}</p>}
        </div>
    );
}
